import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, MessageSquare, FileText, Mail, MapPin, Car, ChevronRight, Download, Send, Building2, Pencil, Trash2, Plus, Archive, Clock, Calendar, DollarSign, Search, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Customer, CustomerVehicle, updateCustomer, createCustomerVehicle, updateCustomerVehicle, deleteCustomerVehicle, deleteCustomer, getCustomer } from '../lib/customers';
import { getBookingsByCustomer, Booking } from '../lib/bookings';
import { getProposalsByCustomer, Proposal } from '../lib/proposals';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from './EmptyState';
import AddressAutocomplete from './AddressAutocomplete';

const tabs = [
  { id: 'overview' as const, label: 'Overview' },
  { id: 'vehicles' as const, label: 'Vehicles' },
  { id: 'jobs' as const, label: 'Jobs' },
  { id: 'proposals' as const, label: 'Proposals' }
] as const;

interface CustomerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onUpdate: () => void;
}

export default function CustomerDetailsModal({ isOpen, onClose, customer, onUpdate }: CustomerDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'vehicles' | 'jobs' | 'proposals'>('overview');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<CustomerVehicle | null>(null);
  const [customerBookings, setCustomerBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [customerProposals, setCustomerProposals] = useState<Proposal[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [vehicleFormData, setVehicleFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '',
    color: '',
    license_plate: '',
    mileage: 0,
    notes: '',
    is_primary: false,
    last_service: null as string | null
  });


  React.useEffect(() => {
    if (isOpen && customer) {
      setIsAnimating(true);
      setFormData(customer);
    }
  }, [isOpen, customer]);

  // Add a new effect to fetch fresh customer data when modal is opened
  React.useEffect(() => {
    if (isOpen && customer) {
      // Refresh the customer data by fetching it again
      const refreshCustomerData = async () => {
        try {
          const freshCustomer = await getCustomer(customer.id);
          setFormData(freshCustomer);
        } catch (err) {
          console.error('Error refreshing customer data:', err);
        }
      };
      
      refreshCustomerData();
    }
  }, [isOpen, customer?.id]);

  // Fetch customer bookings when modal opens or customer changes
  const loadCustomerBookings = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingBookings(true);
      const bookings = await getBookingsByCustomer(selectedBusiness.id, customer.id);
      setCustomerBookings(bookings);
    } catch (err) {
      console.error('Error loading customer bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Fetch customer proposals when modal opens or customer changes
  const loadCustomerProposals = async () => {
    if (!customer?.id || !selectedBusiness?.id) return;

    try {
      setLoadingProposals(true);
      const proposals = await getProposalsByCustomer(selectedBusiness.id, customer.id);
      setCustomerProposals(proposals);
    } catch (err) {
      console.error('Error loading customer proposals:', err);
    } finally {
      setLoadingProposals(false);
    }
  };

  React.useEffect(() => {
    if (isOpen && customer?.id && selectedBusiness?.id) {
      loadCustomerBookings();
      loadCustomerProposals();
    }
  }, [isOpen, customer?.id, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setEditMode(null);
      setError(null);
      setIsAddingVehicle(false);
      setEditingVehicle(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = async (section: string) => {
    if (!customer) return;

    try {
      setLoading(true);
      setError(null);

      await updateCustomer(customer.id, formData);
      setEditMode(null);
      onUpdate();
      
      // Fetch the fresh customer data to update the modal display
      try {
        const freshCustomer = await getCustomer(customer.id);
        setFormData(freshCustomer);
      } catch (err) {
        console.error('Error refreshing customer data after save:', err);
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;

    try {
      setLoading(true);
      setError(null);

      await createCustomerVehicle({
        customer_id: customer.id,
        ...vehicleFormData
      });

      setIsAddingVehicle(false);
      setVehicleFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vin: '',
        color: '',
        license_plate: '',
        mileage: 0,
        notes: '',
        is_primary: false,
        last_service: null
      });
      onUpdate();
    } catch (err) {
      console.error('Error adding vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer || !editingVehicle) return;

    try {
      setLoading(true);
      setError(null);

      await updateCustomerVehicle(editingVehicle.id, vehicleFormData);
      
      setEditingVehicle(null);
      setVehicleFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vin: '',
        color: '',
        license_plate: '',
        mileage: 0,
        notes: '',
        is_primary: false,
        last_service: null
      });
      onUpdate();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to update vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      setLoading(true);
      setError(null);

      await deleteCustomerVehicle(vehicleId);
      onUpdate();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVehicle = (vehicle: CustomerVehicle) => {
    setEditingVehicle(vehicle);
    setVehicleFormData({
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || new Date().getFullYear(),
      color: vehicle.color || '',
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      mileage: vehicle.mileage || 0,
      notes: vehicle.notes || '',
      is_primary: vehicle.is_primary || false,
      last_service: vehicle.last_service || null,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100/80 text-blue-700';
      case 'in_progress':
        return 'bg-yellow-100/80 text-yellow-700';
      case 'completed':
        return 'bg-green-100/80 text-green-700';
      case 'cancelled':
        return 'bg-red-100/80 text-red-700';
      default:
        return 'bg-gray-100/80 text-gray-700';
    }
  };

  const getProposalStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100/80 text-gray-700';
      case 'sent':
        return 'bg-blue-100/80 text-blue-700';
      case 'viewed':
        return 'bg-purple-100/80 text-purple-700';
      case 'approved':
        return 'bg-green-100/80 text-green-700';
      case 'declined':
        return 'bg-red-100/80 text-red-700';
      default:
        return 'bg-gray-100/80 text-gray-700';
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!customer) return null;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onTransitionEnd={handleTransitionEnd}
      >
        <div 
          className={`fixed inset-0 ios-modal-content transition-colors duration-200 ${
            isOpen ? 'bg-gray-50' : 'bg-black'
          }`}
          style={{ 
            WebkitBackfaceVisibility: 'hidden',
            zIndex: 99950,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: isOpen ? 'block' : 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {customer.first_name} {customer.last_name}
                    </h2>
                    <div className="flex items-center mt-1">
                      <div className="h-2 w-2 rounded-full bg-green-400 mr-2" />
                      <span className="text-sm text-gray-300">Active Customer</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this customer? They will be marked as inactive but their data will be preserved.')) {
                        try {
                          console.log('🗑️ Deleting customer:', customer?.id);
                          setLoading(true);
                          
                          // Pass the complete customer data with status update to avoid validation errors
                          await updateCustomer(customer.id, {
                            ...customer,
                            status: 'inactive'
                          });
                          console.log('✅ Customer marked as inactive successfully');
                          onUpdate();
                          onClose();
                        } catch (err) {
                          console.error('❌ Error marking customer as inactive:', err);
                          alert('Failed to delete customer: ' + (err instanceof Error ? err.message : 'Unknown error'));
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200">
              <div className="max-w-5xl mx-auto px-6">
                <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)'
            }}>
              <div className="max-w-5xl mx-auto py-6 px-6" style={{
                paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
              }}>
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {activeTab === 'overview' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                        {editMode !== 'basic' ? (
                          <button
                            onClick={() => setEditMode('basic')}
                            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            Edit
                          </button>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditMode(null)}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSave('basic')}
                              disabled={loading}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {loading ? (
                                <>
                                  <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                  Saving...
                                </>
                              ) : (
                                'Save'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        {editMode !== 'basic' ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                              <div className="text-sm font-medium text-gray-500 mb-1">First Name</div>
                              <div className="text-base font-semibold text-gray-900">{customer.first_name}</div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-500 mb-1">Last Name</div>
                              <div className="text-base font-semibold text-gray-900">{customer.last_name}</div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-500 mb-1">Email</div>
                              <div className="text-base font-semibold text-gray-900">{customer.email}</div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-500 mb-1">Phone</div>
                              <div className="text-base font-semibold text-gray-900">{customer.phone}</div>
                            </div>
                            {customer.address && (
                              <div className="sm:col-span-2">
                                <div className="text-sm font-medium text-gray-500 mb-1">Address</div>
                                <div className="text-base font-semibold text-gray-900">{customer.address}</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                              <input
                                type="text"
                                id="first_name"
                                value={formData.first_name || ''}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                            </div>
                            <div>
                              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                              <input
                                type="text"
                                id="last_name"
                                value={formData.last_name || ''}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                            </div>
                            <div>
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                              <input
                                type="email"
                                id="email"
                                value={formData.email || ''}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                            </div>
                            <div>
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                              <input
                                type="tel"
                                id="phone"
                                value={formData.phone || ''}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                              <AddressAutocomplete
                                value={formData.address || ''}
                                onChange={(address) => setFormData({ ...formData, address })}
                                onSelect={(address) => setFormData({ 
                                  ...formData, 
                                  address: address.formatted,
                                  lat: address.lat,
                                  lng: address.lng
                                })}
                                placeholder="Enter address (optional)"
                                className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'vehicles' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Vehicles</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-lg px-2 py-1">
                            {customer.vehicles?.length || 0}
                          </span>
                        </div>
                        <button
                          onClick={() => setIsAddingVehicle(true)}
                          className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                          Add Vehicle
                        </button>
                      </div>
                      <div className="p-6">
                        {isAddingVehicle && (
                          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <form onSubmit={handleAddVehicle} className="space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Make
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={vehicleFormData.make}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, make: e.target.value }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Model
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={vehicleFormData.model}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, model: e.target.value }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Year
                                  </label>
                                  <input
                                    type="number"
                                    required
                                    min="1900"
                                    max={new Date().getFullYear() + 1}
                                    value={vehicleFormData.year}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Color
                                  </label>
                                  <input
                                    type="text"
                                    value={vehicleFormData.color}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, color: e.target.value }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    VIN
                                  </label>
                                  <input
                                    type="text"
                                    value={vehicleFormData.vin}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, vin: e.target.value }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    License Plate
                                  </label>
                                  <input
                                    type="text"
                                    value={vehicleFormData.license_plate}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Mileage
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={vehicleFormData.mileage}
                                  onChange={(e) => setVehicleFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) }))}
                                  className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Notes
                                </label>
                                <textarea
                                  value={vehicleFormData.notes}
                                  onChange={(e) => setVehicleFormData(prev => ({ ...prev, notes: e.target.value }))}
                                  rows={3}
                                  className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                />
                              </div>
                              <div>
                                <label className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={vehicleFormData.is_primary}
                                    onChange={(e) => setVehicleFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm font-semibold text-gray-700">Set as primary vehicle</span>
                                </label>
                              </div>
                              <div className="flex justify-end space-x-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsAddingVehicle(false);
                                    setVehicleFormData({
                                      make: '',
                                      model: '',
                                      year: new Date().getFullYear(),
                                      vin: '',
                                      color: '',
                                      license_plate: '',
                                      mileage: 0,
                                      notes: '',
                                      is_primary: false,
                                      last_service: null
                                    });
                                  }}
                                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  disabled={loading}
                                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {loading ? (
                                    <>
                                      <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                      Adding...
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="h-4 w-4 mr-2" />
                                      Add Vehicle
                                    </>
                                  )}
                                </button>
                              </div>
                            </form>
                          </div>
                        )}

                        <div className="space-y-4">
                          {customer.vehicles?.map((vehicle) => (
                            <div key={vehicle.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              {editingVehicle?.id === vehicle.id ? (
                                <form onSubmit={handleUpdateVehicle} className="space-y-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        Make
                                      </label>
                                      <input
                                        type="text"
                                        required
                                        value={vehicleFormData.make}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, make: e.target.value }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        Model
                                      </label>
                                      <input
                                        type="text"
                                        required
                                        value={vehicleFormData.model}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, model: e.target.value }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        Year
                                      </label>
                                      <input
                                        type="number"
                                        required
                                        min="1900"
                                        max={new Date().getFullYear() + 1}
                                        value={vehicleFormData.year}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        Color
                                      </label>
                                      <input
                                        type="text"
                                        value={vehicleFormData.color}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, color: e.target.value }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        VIN
                                      </label>
                                      <input
                                        type="text"
                                        value={vehicleFormData.vin}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, vin: e.target.value }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        License Plate
                                      </label>
                                      <input
                                        type="text"
                                        value={vehicleFormData.license_plate}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                                        className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                        style={{ 
                                          minHeight: '44px',
                                          fontSize: '16px',
                                          backdropFilter: 'blur(10px)',
                                          WebkitBackdropFilter: 'blur(10px)'
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                      Mileage
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={vehicleFormData.mileage}
                                      onChange={(e) => setVehicleFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) }))}
                                      className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                                      style={{ 
                                        minHeight: '44px',
                                        fontSize: '16px',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)'
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                      Notes
                                    </label>
                                    <textarea
                                      value={vehicleFormData.notes}
                                      onChange={(e) => setVehicleFormData(prev => ({ ...prev, notes: e.target.value }))}
                                      rows={3}
                                      className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation resize-none"
                                      style={{ 
                                        minHeight: '100px',
                                        fontSize: '16px',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)'
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="flex items-center space-x-3">
                                      <input
                                        type="checkbox"
                                        checked={vehicleFormData.is_primary}
                                        onChange={(e) => setVehicleFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                                        className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                      />
                                      <span className="text-sm font-semibold text-gray-700">Set as primary vehicle</span>
                                    </label>
                                  </div>
                                  <div className="flex justify-end space-x-3">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingVehicle(null);
                                        setVehicleFormData({
                                          make: '',
                                          model: '',
                                          year: new Date().getFullYear(),
                                          vin: '',
                                          color: '',
                                          license_plate: '',
                                          mileage: 0,
                                          notes: '',
                                          is_primary: false,
                                          last_service: null
                                        });
                                      }}
                                      className="inline-flex items-center justify-center px-4 py-3 text-sm font-bold text-gray-600 bg-gray-100/80 hover:bg-gray-200/80 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                                      style={{ minHeight: '44px' }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="submit"
                                      disabled={loading}
                                      className="inline-flex items-center justify-center px-4 py-3 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation disabled:opacity-50"
                                      style={{ minHeight: '44px' }}
                                    >
                                      {loading ? (
                                        <>
                                          <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                          Saving...
                                        </>
                                      ) : (
                                        'Save Changes'
                                      )}
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <div className="text-sm font-bold text-gray-900">
                                          {vehicle.year} {vehicle.make} {vehicle.model}
                                        </div>
                                        {vehicle.is_primary && (
                                          <span className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-100 text-blue-700">
                                            Primary
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-600 mt-2 space-y-1">
                                        {vehicle.license_plate && (
                                          <div>License: <span className="font-semibold">{vehicle.license_plate}</span></div>
                                        )}
                                        {vehicle.vin && (
                                          <div>VIN: <span className="font-semibold">{vehicle.vin}</span></div>
                                        )}
                                        {vehicle.color && (
                                          <div>Color: <span className="font-semibold">{vehicle.color}</span></div>
                                        )}
                                        {vehicle.notes && (
                                          <div className="mt-2 text-sm text-gray-500">
                                            {vehicle.notes}
                                          </div>
                                        )}
                                        {vehicle.last_service && (
                                          <div className="mt-2">
                                            <div className="text-xs font-bold text-gray-500">Last Service</div>
                                            <div className="text-sm font-semibold text-gray-900">
                                              {new Date(vehicle.last_service).toLocaleDateString()}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2 ml-4">
                                    <button
                                      onClick={() => {
                                        handleEditVehicle(vehicle);
                                      }}
                                      className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteVehicle(vehicle.id)}
                                      className="flex items-center justify-center w-10 h-10 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {customer.vehicles?.length === 0 && !isAddingVehicle && (
                            <div className="py-8 text-center">
                              <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No vehicles added yet</div>
                              <div className="text-sm text-gray-400 mt-1">Add a vehicle to get started</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'jobs' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Booking History</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-lg px-2 py-1">
                            {customerBookings.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {/* Mobile Booking List */}
                        <div className="sm:hidden">
                          {loadingBookings ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading bookings...</p>
                              </div>
                            </div>
                          ) : customerBookings.length === 0 ? (
                            <div className="py-8 text-center">
                              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No bookings found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't made any bookings yet</div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {customerBookings.map((booking) => (
                                <div key={booking.id}>
                                  <div 
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                      // Navigate to bookings page and pass booking ID
                                      navigate('/bookings', { state: { selectedBookingId: booking.id } });
                                    }}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                                        {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-bold text-gray-900 truncate">
                                            #{booking.booking_number}
                                          </div>
                                          <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-lg ${getStatusColor(booking.status)}`}>
                                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                          </span>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600">
                                          {booking.services?.map((service, index) => (
                                            <div key={index} className="truncate font-medium">
                                              {service.name}
                                              <span className="text-gray-400"> • </span>
                                              {service.vehicle_make} {service.vehicle_model}
                                            </div>
                                          ))}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                          <div className="text-sm text-gray-500 font-medium">
                                            {booking.start_date && `${booking.start_date} ${booking.start_time}`}
                                          </div>
                                          <div className="text-sm font-bold text-gray-900">
                                            ${booking.total_amount?.toLocaleString() || '0.00'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Desktop Table */}
                        <div className="hidden sm:block">
                          {loadingBookings ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading bookings...</p>
                              </div>
                            </div>
                          ) : customerBookings.length === 0 ? (
                            <div className="py-8 text-center">
                              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No bookings found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't made any bookings yet</div>
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-lg border border-gray-200">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Booking #
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Services
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {customerBookings.map((booking) => (
                                    <tr
                                      key={booking.id}
                                      className="border-t border-gray-200 hover:bg-gray-50 active:bg-purple-100/50 transition-all duration-200 cursor-pointer"
                                      onClick={() => {
                                        // Navigate to bookings page and pass booking ID
                                        navigate('/bookings', { state: { selectedBookingId: booking.id } });
                                      }}
                                    >
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        #{booking.booking_number}
                                      </td>
                                      <td className="px-4 py-4">
                                        <div className="space-y-1">
                                          {booking.services?.map((service, index) => (
                                            <div key={`${service.id}-${index}`} className="text-sm">
                                              <span className="font-semibold text-gray-900">
                                                {service.name}
                                              </span>
                                              <span className="text-gray-500 ml-1">
                                                ({service.vehicle_make} {service.vehicle_model})
                                              </span>
                                              {index < (booking.services?.length || 0) - 1 && <br />}
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                                        {booking.start_date &&
                                          `${booking.start_date} ${booking.start_time}`}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span
                                          className={`px-3 py-1 inline-flex text-sm font-medium rounded-lg ${getStatusColor(
                                            booking.status
                                          )}`}
                                        >
                                          {booking.status.charAt(0).toUpperCase() +
                                            booking.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        ${booking.total_amount?.toLocaleString() || '0.00'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'proposals' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">Proposal History</h3>
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-lg px-2 py-1">
                            {customerProposals.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {/* Mobile Proposal List */}
                        <div className="sm:hidden">
                          {loadingProposals ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading proposals...</p>
                              </div>
                            </div>
                          ) : customerProposals.length === 0 ? (
                            <div className="py-8 text-center">
                              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No proposals found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't received any proposals yet</div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {customerProposals.map((proposal) => (
                                <div key={proposal.id}>
                                  <div 
                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                      // Navigate to proposals page and pass proposal ID
                                      navigate('/proposals', { state: { selectedProposalId: proposal.id } });
                                    }}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                                        <FileText className="h-5 w-5" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-bold text-gray-900 truncate">
                                            {proposal.title}
                                          </div>
                                          <span className={`ml-2 px-3 py-1 text-xs font-medium rounded-lg ${getProposalStatusColor(proposal.status)}`}>
                                            {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                                          </span>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                          <div className="text-sm text-gray-500 font-medium">
                                            {new Date(proposal.created_at).toLocaleDateString()}
                                          </div>
                                          <div className="text-sm font-bold text-gray-900">
                                            ${proposal.total_amount.toLocaleString()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Desktop Table */}
                        <div className="hidden sm:block">
                          {loadingProposals ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="text-center">
                                <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-sm font-semibold text-gray-600">Loading proposals...</p>
                              </div>
                            </div>
                          ) : customerProposals.length === 0 ? (
                            <div className="py-8 text-center">
                              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <div className="text-sm font-semibold text-gray-500">No proposals found</div>
                              <div className="text-sm text-gray-400 mt-1">This customer hasn't received any proposals yet</div>
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-lg border border-gray-200">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Customer
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Title
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {customerProposals.map((proposal) => (
                                    <tr
                                      key={proposal.id}
                                      className="border-t border-gray-200 hover:bg-gray-50 active:bg-purple-100/50 transition-all duration-200 cursor-pointer"
                                      onClick={() => {
                                        // Navigate to proposals page and pass proposal ID
                                        navigate('/proposals', { state: { selectedProposalId: proposal.id } });
                                      }}
                                    >
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                                            {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                                          </div>
                                          <div className="ml-4">
                                            <div className="text-sm font-bold text-gray-900">
                                              {proposal.customer?.first_name} {proposal.customer?.last_name}
                                            </div>
                                            <div className="text-sm text-gray-600 font-medium">
                                              {proposal.customer?.email}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        {proposal.title}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span
                                          className={`px-3 py-1 inline-flex text-sm font-medium rounded-lg ${getProposalStatusColor(
                                            proposal.status
                                          )}`}
                                        >
                                          {proposal.status.charAt(0).toUpperCase() +
                                            proposal.status.slice(1)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        ${proposal.total_amount.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}